/**
 * Video Panel
 *
 * Self-contained video display for use in the unified session viewer.
 * Wraps LiveKit room connection with zoom/filter controls.
 * Shares settings with standalone screen-stream viewer.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
	ZoomIn,
	ZoomOut,
	Lock,
	Unlock,
	Loader2,
	WifiOff,
	Wifi,
	MonitorPlay,
} from 'lucide-react'
import {
	LiveKitRoom,
	useConnectionState,
	useTracks,
	VideoTrack,
} from '@livekit/components-react'
import { type BoxSize, containedSize } from '@polygraf/shared'
import { ConnectionState, Track } from 'livekit-client'
import { useTranslation } from 'react-i18next'
import { useLivekitRoom } from '../../hooks/use-livekit-room'
import { useZoomPan } from '../../hooks/use-zoom-pan'
import { IosVideoFix } from './ios-video-fix'
import {
	type VideoSettings,
	loadVideoSettings,
	saveVideoSettings,
} from './video-settings'

/**
 * Keep zoom on whole tenths. 0.1 is not exact in binary, so repeated steps
 * drifted to values like 0.9999999999999999 that read "100%" but scale the
 * picture a hair off and never compare equal to 1.0 again.
 */
function roundZoom(value: number): number {
	return Math.round(value * 10) / 10
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 3.0

interface VideoPanelProps {
	sessionId: string
	className?: string
	/** Immersive mode: the control bar is hidden with the page toolbar. */
	immersive?: boolean
	onConnectionChange?: (connected: boolean) => void
}

export function VideoPanel({
	sessionId,
	className = '',
	immersive = false,
	onConnectionChange,
}: VideoPanelProps) {
	const { token, serverUrl, isLoading, error: tokenError, retry } = useLivekitRoom({
		roomName: sessionId,
		role: 'viewer',
		enabled: !!sessionId,
	})

	return (
		<div className={`${className}`}>
			<LiveKitRoom
				token={token ?? undefined}
				serverUrl={serverUrl ?? undefined}
				connect={!!token}
				className="h-full"
				onError={(err) => {
					console.error('LiveKit room error:', err)
				}}
			>
				<VideoContent
					tokenLoading={isLoading}
					tokenError={tokenError}
					retryToken={retry}
					immersive={immersive}
					onConnectionChange={onConnectionChange}
				/>
			</LiveKitRoom>
		</div>
	)
}

interface VideoContentProps {
	tokenLoading: boolean
	tokenError: string | null
	retryToken: () => void
	immersive: boolean
	onConnectionChange?: (connected: boolean) => void
}

function VideoContent({
	tokenLoading,
	tokenError,
	retryToken,
	immersive,
	onConnectionChange,
}: VideoContentProps) {
	const { t } = useTranslation()
	const connectionState = useConnectionState()
	const tracks = useTracks([Track.Source.ScreenShare, Track.Source.Camera])

	const [settings, setSettings] = useState<VideoSettings>(loadVideoSettings)
	const videoContainerRef = useRef<HTMLDivElement>(null)

	const isConnected = connectionState === ConnectionState.Connected
	const isConnecting = connectionState === ConnectionState.Connecting || tokenLoading
	const remoteTrack = tracks.find((t) => !t.participant.isLocal)
	const hasBroadcaster = !!remoteTrack

	// Report connection status to parent
	useEffect(() => {
		onConnectionChange?.(isConnected && hasBroadcaster)
	}, [isConnected, hasBroadcaster, onConnectionChange])

	useEffect(() => {
		saveVideoSettings(settings)
	}, [settings])

	const zoomIn = useCallback(() => {
		setSettings((prev) => {
			if (prev.zoomLocked) return prev
			return { ...prev, zoomLevel: roundZoom(Math.min(MAX_ZOOM, prev.zoomLevel + 0.1)) }
		})
	}, [])

	const zoomOut = useCallback(() => {
		setSettings((prev) => {
			if (prev.zoomLocked) return prev
			return { ...prev, zoomLevel: roundZoom(Math.max(MIN_ZOOM, prev.zoomLevel - 0.1)) }
		})
	}, [])

	const resetZoom = useCallback(() => {
		setSettings((prev) => {
			if (prev.zoomLocked) return prev
			return { ...prev, zoomLevel: 1.0 }
		})
	}, [])

	const toggleZoomLock = useCallback(() => {
		setSettings((prev) => ({ ...prev, zoomLocked: !prev.zoomLocked }))
	}, [])

	/**
	 * Pan the zoomed live video (MU, 25. 9. 2026). The zoom buttons scaled it
	 * about its centre inside an overflow-hidden box and nothing moved it, so
	 * at 200 % half the slide was simply out of reach. Same gestures as the
	 * image viewer: one finger or the mouse drags 1:1, two fingers pan and
	 * pinch around their midpoint, arrow keys pan when the video has focus.
	 */
	const setPinchZoom = useCallback((zoomLevel: number) => {
		setSettings((prev) => (prev.zoomLevel === zoomLevel ? prev : { ...prev, zoomLevel }))
	}, [])
	// The video is `object-contain`: a 16:9 slide on a tall phone pane is a
	// band with black bars above and below. Pan limits come from that band,
	// not the pane, or the reader could pan the slide off into the bars.
	const videoContentSize = useCallback((pane: BoxSize) => {
		const video = videoContainerRef.current?.querySelector('video')
		return video
			? containedSize({ width: video.videoWidth, height: video.videoHeight }, pane)
			: pane
	}, [])
	const zoomPan = useZoomPan({
		paneRef: videoContainerRef,
		zoom: settings.zoomLevel,
		zoomLocked: settings.zoomLocked,
		minZoom: MIN_ZOOM,
		maxZoom: MAX_ZOOM,
		onZoomChange: setPinchZoom,
		contentSize: videoContentSize,
	})
	const canPan = hasBroadcaster && settings.zoomLevel > 1

	const toggleGrayscale = useCallback(() => {
		setSettings((prev) => ({ ...prev, grayscale: !prev.grayscale }))
	}, [])

	const toggleInvert = useCallback(() => {
		setSettings((prev) => ({ ...prev, inverted: !prev.inverted }))
	}, [])

	const handleWheel = (e: React.WheelEvent) => {
		if (settings.zoomLocked) return
		e.preventDefault()
		if (e.deltaY > 0) {
			zoomOut()
		} else {
			zoomIn()
		}
	}

	const videoFilter = [
		settings.grayscale ? 'grayscale(1)' : '',
		settings.inverted ? 'invert(1)' : '',
	].filter(Boolean).join(' ') || 'none'

	return (
		<div className="flex flex-col bg-black overflow-hidden h-full">
			{/* Video area — clip zoom overflow to this box. touch-action none only
			    while the video is zoomed above 100 %: then the fingers pan and pinch
			    the video, and the browser must not scroll or zoom the page under
			    them (that would also cancel the gesture mid-way). At or below 100 %
			    the browser keeps scrolling and pinch-zooming the page from here
			    (manipulation: only its double-tap zoom is off, for the immersive
			    double tap). Focusable only while zoomed, so keyboard users can pan
			    it with the arrow keys. */}
			{/* biome-ignore lint/a11y/useSemanticElements: a fieldset groups form
			    controls; this is a labelled picture, and IosVideoFix needs a div ref. */}
			<div
				ref={videoContainerRef}
				className={`flex-1 min-h-0 relative overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70`}
				style={hasBroadcaster ? { touchAction: zoomPan.touchAction } : undefined}
				onWheel={handleWheel}
				{...(hasBroadcaster ? zoomPan.handlers : {})}
				tabIndex={canPan ? 0 : undefined}
				role="group"
				aria-label={canPan ? t('viewer.screen.zoomedLiveVideo') : undefined}
				aria-keyshortcuts={canPan ? 'ArrowUp ArrowDown ArrowLeft ArrowRight' : undefined}
			>
				{hasBroadcaster ? (
					<div
						className="absolute inset-0"
						style={{
							transform: zoomPan.transform,
							filter: videoFilter,
							transformOrigin: 'center center',
							cursor: canPan ? 'grab' : undefined,
						}}
					>
						<VideoTrack
							trackRef={remoteTrack}
							className="!w-full !h-full [&>video]:w-full [&>video]:h-full [&>video]:object-contain"
							manageSubscription={false}
						/>
						{/* iOS Safari requires playsInline for inline video playback */}
						<IosVideoFix containerRef={videoContainerRef} />
					</div>
				) : (
					<div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 space-y-3 p-8">
						{isConnecting ? (
							<>
								<Loader2 size={40} className="animate-spin mx-auto text-white/40" />
								<p className="text-sm">{t('viewer.status.connecting')}…</p>
							</>
						) : tokenError ? (
							<>
								<WifiOff size={40} className="mx-auto text-white/30" />
								<p className="text-sm">{t('viewer.videoPanel.connectionFailed')}</p>
								<p className="text-xs text-red-400">{tokenError}</p>
								<button
									className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-white/60 hover:text-white border border-white/20 hover:bg-white/10 transition-colors"
									onClick={retryToken}
								>
									{t('viewer.transcript.reconnect')}
								</button>
							</>
						) : !isConnected ? (
							<>
								<WifiOff size={40} className="mx-auto text-white/30" />
								<p className="text-sm">{t('viewer.screenStream.disconnected')}</p>
								<button
									className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-white/60 hover:text-white border border-white/20 hover:bg-white/10 transition-colors"
									onClick={retryToken}
								>
									{t('viewer.transcript.reconnect')}
								</button>
							</>
						) : (
							<>
								<MonitorPlay size={40} className="mx-auto text-white/30" />
								<p className="text-sm">{t('viewer.screenStream.waitingForBroadcaster')}</p>
								<p className="text-xs text-white/40">
									{t('viewer.screenStream.waitingForBroadcasterDesc')}
								</p>
							</>
						)}
					</div>
				)}
			</div>

			{/* Compact control bar */}
			<div
				className={`${immersive ? 'hidden' : 'flex'} items-center justify-between px-3 py-2 bg-black/90 border-t border-white/10 shrink-0`}
			>
				<div className="flex items-center gap-1 text-white/60 text-xs">
					{isConnected ? (
						<Wifi size={12} className="text-green-400" />
					) : (
						<WifiOff size={12} className="text-red-400" />
					)}
					<span>{hasBroadcaster
							? t('viewer.status.live')
							: isConnected
								? t('viewer.status.waiting')
								: t('viewer.status.offline')}</span>
					{/* MP-187: who is presenting — LiveKit participant name minted server-side. */}
					{hasBroadcaster && remoteTrack?.participant.name && (
						<span className="text-white/40 truncate max-w-[10rem]">
							· {remoteTrack.participant.name}
						</span>
					)}
				</div>

				<div className="flex items-center gap-0.5">
					<button
						className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
						onClick={zoomOut}
						disabled={settings.zoomLocked}
						title={t('viewer.keyboard.zoomOut')}
						aria-label={t('viewer.keyboard.zoomOut')}
					>
						<ZoomOut size={12} />
					</button>
					<button
						className="inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors font-mono text-xs"
						onClick={resetZoom}
						disabled={settings.zoomLocked}
						title={t('viewer.videoPanel.resetZoom')}
						aria-label={t('viewer.videoPanel.resetZoom')}
					>
						{Math.round(settings.zoomLevel * 100)}%
					</button>
					<button
						className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
						onClick={zoomIn}
						disabled={settings.zoomLocked}
						title={t('viewer.keyboard.zoomIn')}
						aria-label={t('viewer.keyboard.zoomIn')}
					>
						<ZoomIn size={12} />
					</button>
					<button
						className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
						onClick={toggleZoomLock}
						title={settings.zoomLocked ? t('viewer.screen.unlockZoom') : t('viewer.screen.lockZoom')}
						aria-label={t('viewer.screen.lockZoom')}
						aria-pressed={settings.zoomLocked}
					>
						{settings.zoomLocked ? <Lock size={12} /> : <Unlock size={12} />}
					</button>

					<div className="w-px h-3 bg-white/20 mx-1" />

					<button
						className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs transition-colors ${settings.grayscale ? 'text-white bg-white/10' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
						onClick={toggleGrayscale}
						title={t('viewer.keyboard.grayscale')}
						aria-label={`${t('viewer.keyboard.grayscale')} (BW)`}
						aria-pressed={settings.grayscale}
					>
						BW
					</button>
					<button
						className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs transition-colors ${settings.inverted ? 'text-white bg-white/10' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
						onClick={toggleInvert}
						title={t('viewer.keyboard.inverted')}
						aria-label={`${t('viewer.keyboard.inverted')} (INV)`}
						aria-pressed={settings.inverted}
					>
						INV
					</button>
				</div>
			</div>
		</div>
	)
}

// IosVideoFix imported from ./ios-video-fix

